import type { Settings } from "../types";

interface SettingsPanelProps {
  settings: Settings;
  setSettings: (updater: (prev: Settings) => Settings) => void;
  onReset: () => void;
}

export default function SettingsPanel({
  settings,
  setSettings,
  onReset
}: SettingsPanelProps) {
  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-3 gap-3">
        <NumberField
          label="专注（分钟）"
          value={settings.focus}
          min={1}
          max={120}
          onChange={(v) => setSettings((s) => ({ ...s, focus: v }))}
          accent="focus"
        />
        <NumberField
          label="短休息"
          value={settings.shortBreak}
          min={1}
          max={60}
          onChange={(v) => setSettings((s) => ({ ...s, shortBreak: v }))}
          accent="short"
        />
        <NumberField
          label="长休息"
          value={settings.longBreak}
          min={1}
          max={60}
          onChange={(v) => setSettings((s) => ({ ...s, longBreak: v }))}
          accent="long"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-body">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={settings.autoStart}
            onChange={(e) => setSettings((s) => ({ ...s, autoStart: e.target.checked }))}
            className="accent-focus w-4 h-4"
          />
          自动开始下一段
        </label>
        <label className="flex items-center gap-2">
          每完成
          <input
            type="number"
            value={settings.longBreakInterval}
            min={2}
            max={10}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                longBreakInterval: Math.max(2, Number(e.target.value) || 4)
              }))
            }
            className="w-12 px-2 py-1 rounded border border-line bg-paper text-center outline-none focus:border-focus focus:ring-4 focus:ring-focus-soft"
          />
          个进入长休息
        </label>
      </div>

      <div className="flex items-center justify-end gap-3 text-sm pt-4 border-t border-hairline">
        <button onClick={onReset} className="text-soft hover:text-focus">
          重置统计数据
        </button>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
  accent
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  accent: "focus" | "short" | "long";
}) {
  const accentRing =
    accent === "focus"
      ? "focus:ring-focus-soft focus:border-focus"
      : accent === "short"
      ? "focus:ring-short-soft focus:border-short"
      : "focus:ring-long-soft focus:border-long";
  const dot = accent === "focus" ? "bg-focus" : accent === "short" ? "bg-short" : "bg-long";
  return (
    <label className="flex flex-col gap-1.5 text-xs text-body">
      <span className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        {label}
      </span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (!Number.isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
        }}
        className={`px-3 py-2 rounded-lg border border-line bg-paper outline-none text-ink text-sm focus:ring-4 ${accentRing}`}
      />
    </label>
  );
}
